import * as React from 'react';
import { Progress as ProgressPrimitive } from '@base-ui/react/progress';

import { cn } from '@/ui/lib/utils';

// React 18 では ref が props として渡らないため forwardRef で包む。React 19 移行時に外す。
// shadcn（base-nova）の Progress を CLI で追加した。クラス値は生成元のまま
// （高さ 4px・指示子 primary）。案件カードでは両端の丸飾りを持たない。
// トラックだけは生成元の muted でなく progress-track トークン（現行の $progress-bg と同じ #dee2e6）にした。
// muted だと白い面の上でトラックがほとんど見えないため。
const ProgressTrack = React.forwardRef<
  HTMLDivElement,
  ProgressPrimitive.Track.Props
>(({ className, ...props }, ref) => (
  <ProgressPrimitive.Track
    ref={ref}
    className={cn(
      'tw:relative tw:flex tw:h-1 tw:w-full tw:items-center tw:overflow-x-hidden tw:rounded-full tw:bg-progress-track',
      className,
    )}
    data-slot="progress-track"
    {...props}
  />
));
ProgressTrack.displayName = 'ProgressTrack';

const ProgressIndicator = React.forwardRef<
  HTMLDivElement,
  ProgressPrimitive.Indicator.Props
>(({ className, ...props }, ref) => (
  <ProgressPrimitive.Indicator
    ref={ref}
    data-slot="progress-indicator"
    className={cn('tw:h-full tw:bg-primary tw:transition-all', className)}
    {...props}
  />
));
ProgressIndicator.displayName = 'ProgressIndicator';

const Progress = React.forwardRef<HTMLDivElement, ProgressPrimitive.Root.Props>(
  ({ className, children, value, ...props }, ref) => (
    <ProgressPrimitive.Root
      ref={ref}
      value={value}
      data-slot="progress"
      className={cn('tw:flex tw:flex-wrap tw:gap-3', className)}
      {...props}
    >
      {children}
      <ProgressTrack>
        <ProgressIndicator />
      </ProgressTrack>
    </ProgressPrimitive.Root>
  ),
);
Progress.displayName = 'Progress';

const ProgressLabel = React.forwardRef<
  HTMLSpanElement,
  ProgressPrimitive.Label.Props
>(({ className, ...props }, ref) => (
  <ProgressPrimitive.Label
    ref={ref}
    className={cn('tw:text-sm tw:font-medium', className)}
    data-slot="progress-label"
    {...props}
  />
));
ProgressLabel.displayName = 'ProgressLabel';

const ProgressValue = React.forwardRef<
  HTMLSpanElement,
  ProgressPrimitive.Value.Props
>(({ className, ...props }, ref) => (
  <ProgressPrimitive.Value
    ref={ref}
    className={cn(
      'tw:ml-auto tw:text-sm tw:text-muted-foreground tw:tabular-nums',
      className,
    )}
    data-slot="progress-value"
    {...props}
  />
));
ProgressValue.displayName = 'ProgressValue';

export {
  Progress,
  ProgressTrack,
  ProgressIndicator,
  ProgressLabel,
  ProgressValue,
};
